import type { CSSProperties } from "react";
import type {
  BlockSpacingBox,
  SpacingSide,
  SpacingUnit,
} from "@/lib/cms-puck/block-style";

/**
 * Padding GLOBAL de la page — stocké dans `layout.root.props.padding`.
 * Structurel (identique toutes langues) → Json opaque côté API.
 *
 * KEEP IN SYNC avec inventozen-frontpublic/src/lib/cms-puck/blocks/page-padding.ts
 *
 * Appliqué sur le conteneur root Puck (contenu de page uniquement — hors
 * header/footer site). Permet d'éviter de répéter le padding sur chaque bloc.
 */

const SPACING_UNITS: readonly SpacingUnit[] = ["px", "rem", "em", "%"];

export type PagePadding = BlockSpacingBox & {
  unit: SpacingUnit;
};

export const DEFAULT_PAGE_PADDING: PagePadding = {
  top: null,
  right: null,
  bottom: null,
  left: null,
  unit: "px",
};

function normalizeSide(value: unknown): SpacingSide {
  if (value == null || value === "") return null;
  const n = typeof value === "number" ? value : Number(value);
  return Number.isFinite(n) ? n : null;
}

/** Normalise `root.props.padding` (partiel / legacy / inconnu). */
export function normalizePagePadding(
  value: Partial<PagePadding> | null | undefined,
): PagePadding {
  if (!value || typeof value !== "object") return { ...DEFAULT_PAGE_PADDING };
  const unit = value.unit;
  return {
    top: normalizeSide(value.top),
    right: normalizeSide(value.right),
    bottom: normalizeSide(value.bottom),
    left: normalizeSide(value.left),
    unit: SPACING_UNITS.includes(unit as SpacingUnit)
      ? (unit as SpacingUnit)
      : "px",
  };
}

/**
 * Style inline padding-* pour le root. Aucune propriété émise si tous les côtés
 * sont null → comportement inchangé pour les pages existantes.
 */
export function resolvePagePaddingStyle(
  padding: PagePadding | null | undefined,
): CSSProperties {
  const box = normalizePagePadding(padding);
  const out: CSSProperties = {};
  if (box.top != null) out.paddingTop = `${box.top}${box.unit}`;
  if (box.right != null) out.paddingRight = `${box.right}${box.unit}`;
  if (box.bottom != null) out.paddingBottom = `${box.bottom}${box.unit}`;
  if (box.left != null) out.paddingLeft = `${box.left}${box.unit}`;
  return out;
}

/** True si au moins un côté a une valeur numérique. */
export function hasPagePadding(padding: PagePadding | null | undefined): boolean {
  const box = normalizePagePadding(padding);
  return (
    box.top != null ||
    box.right != null ||
    box.bottom != null ||
    box.left != null
  );
}
